import * as THREE from "three";
import { textureUrl } from "../assets/manifest.js";
import { fieldSurface } from "./fieldSurface.js";

type PavingVariant = "default" | "ember" | "frost" | "carnival";
interface TerracePalette { variant: PavingVariant; tile: number; footing: number; kerb: number; }
const DEFAULT: TerracePalette = { variant: "default", tile: 0x73766d, footing: 0x887b60, kerb: 0xaaa999 };
const THEMES: Record<string, TerracePalette> = {
  "cos.base_skin.ember": { variant: "ember", tile: 0xc9905f, footing: 0xa96441, kerb: 0xedcb8d },
  "cos.base_skin.frost": { variant: "frost", tile: 0xbddce0, footing: 0x769cad, kerb: 0xe1f4f1 },
  "cos.base_skin.carnival": { variant: "carnival", tile: 0xb4a3cb, footing: 0x79658d, kerb: 0xeacb92 },
};

export interface TerraceState {
  status: "loading" | "ready" | "error" | "disposed";
  skin: string | null;
  error: string | null;
}
interface Control {
  size: number;
  generation: number;
  disposed: boolean;
  state: TerraceState;
  ready: Promise<void>;
  cancel: (() => void) | null;
  paving: THREE.MeshStandardMaterial;
  listeners: Set<(state: TerraceState) => void>;
}
const controls = new WeakMap<THREE.Object3D, Control>();

export function terracePalette(id: string | null): TerracePalette {
  return (id ? THEMES[id] : null) ?? DEFAULT;
}

/** One texture belongs to one terrace. Browser HTTP caching shares bytes, never disposal ownership. */
async function loadPavingTexture(variant: PavingVariant, size: number): Promise<THREE.Texture> {
  const texture = await new THREE.TextureLoader().loadAsync(textureUrl("terrain.paving", variant));
  try {
    const image = texture.image as HTMLImageElement | undefined;
    if (!image || typeof image.decode !== "function") throw new Error("Paving texture is not a decodable image");
    await image.decode();
    if (image.naturalWidth !== 512 || image.naturalHeight !== 512) throw new Error("Paving texture has invalid dimensions");
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(size / 8, size / 8);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.needsUpdate = true;
    return texture;
  } catch (error) {
    texture.dispose();
    throw error;
  }
}

function publish(root: THREE.Object3D, control: Control, status: TerraceState["status"], error: string | null = null): void {
  control.state = { status, skin: control.state.skin, error };
  root.userData["pavingState"] = status;
  for (const listener of control.listeners) listener(control.state);
}

function requestSkin(root: THREE.Object3D, skin: string | null): void {
  const control = controls.get(root);
  if (!control || control.disposed) return;
  control.cancel?.();
  const generation = ++control.generation;
  const palette = terracePalette(skin);
  control.state = { status: "loading", skin, error: null };
  root.userData["skin"] = skin;
  root.userData["pavingRequestedVariant"] = palette.variant;
  control.ready = new Promise<void>((resolve, reject) => {
    control.cancel = () => reject(new Error("Terrace texture request superseded or disposed"));
    void loadPavingTexture(palette.variant, control.size).then((texture) => {
      if (control.disposed || generation !== control.generation) { texture.dispose(); return; }
      control.cancel = null;
      const previous = control.paving.map;
      control.paving.map = texture;
      control.paving.color.set(0xffffff);
      control.paving.needsUpdate = true;
      root.traverse((object) => {
        if (!(object instanceof THREE.Mesh) || !(object.material instanceof THREE.MeshStandardMaterial)) return;
        if (object.name === "enclave.footing") object.material.color.set(palette.footing);
        if (object.name === "enclave.kerb") { object.material.color.set(palette.kerb); object.visible = palette.variant !== "default"; }
      });
      root.userData["pavingVariant"] = palette.variant;
      previous?.dispose();
      publish(root, control, "ready");
      resolve();
    }, (error: unknown) => {
      if (control.disposed || generation !== control.generation) return;
      control.cancel = null;
      publish(root, control, "error", error instanceof Error ? error.message : String(error));
      reject(error);
    });
  });
  // Live scenes observe state rather than await readiness. Retain the rejection for
  // explicit awaiters without an unhandled promise when a scene is being removed.
  void control.ready.catch(() => {});
  publish(root, control, "loading");
}

/** Both live enclave and material preview use the same model-derived PNGs and cell scale. */
export function createEnclaveTerrace(size = 24, skin: string | null = null): THREE.Group {
  const palette = terracePalette(skin);
  const group = new THREE.Group();
  group.name = "enclave.terrace";
  group.userData["skin"] = skin;
  group.userData["size"] = size;
  // A bank slopes into the valley instead of presenting the whole settlement as a boxed board.
  const bankVertices: number[] = [], bankFaces: number[] = [];
  const corners = [[-1,-1],[1,-1],[1,1],[-1,1]] as const;
  for (let side=0; side<4; side++) {
    const a=corners[side]!, b=corners[(side+1)%4]!;
    for (let i=0; i<8; i++) {
      const t=i/8, x=a[0]+(b[0]-a[0])*t, z=a[1]+(b[1]-a[1])*t;
      const spread=.54+.16*Math.cos((side*8+i)*2.4);
      bankVertices.push(x*size/2, -.004, z*size/2, x*(size/2+spread), -.12, z*(size/2+spread));
      const j=(side*8+i)*2, next=((side*8+i+1)%32)*2;
      bankFaces.push(j,next,j+1, next,next+1,j+1);
    }
  }
  const bank = new THREE.BufferGeometry();
  bank.setAttribute("position", new THREE.Float32BufferAttribute(bankVertices,3));bank.setIndex(bankFaces);bank.computeVertexNormals();
  const footing = new THREE.Mesh(bank, fieldSurface(palette.footing, "yard"));
  footing.name = "enclave.footing";
  footing.receiveShadow = true;
  const material = new THREE.MeshStandardMaterial({ color: palette.tile, roughness: 0.95, metalness: 0 });
  const top = new THREE.Mesh(new THREE.PlaneGeometry(size, size), material);
  top.name = "enclave.paving";
  top.rotation.x = -Math.PI / 2;
  top.receiveShadow = true;
  group.add(footing, top);
  const kerbMaterial = new THREE.MeshStandardMaterial({ color: palette.kerb, roughness: 0.85 });
  for (const [x, z, w, d] of [[0, -size / 2 - 0.25, size + 1, 0.4], [0, size / 2 + 0.25, size + 1, 0.4],
    [-size / 2 - 0.25, 0, 0.4, size], [size / 2 + 0.25, 0, 0.4, size]] as const) {
    const kerb = new THREE.Mesh(new THREE.BoxGeometry(w, 0.16, d), kerbMaterial);
    kerb.name = "enclave.kerb";
    kerb.visible = palette.variant !== "default";
    kerb.position.set(x, -0.04, z);
    kerb.receiveShadow = true;
    group.add(kerb);
  }
  controls.set(group, { size, generation: 0, disposed: false, state: { status: "loading", skin, error: null },
    ready: Promise.resolve(), cancel: null, paving: material, listeners: new Set() });
  requestSkin(group, skin);
  return group;
}

export function terraceState(root: THREE.Object3D): TerraceState {
  return controls.get(root)?.state ?? { status: "disposed", skin: null, error: null };
}

export function subscribeTerraceState(root: THREE.Object3D, listener: (state: TerraceState) => void): () => void {
  const control = controls.get(root);
  listener(terraceState(root));
  if (!control || control.disposed) return () => {};
  control.listeners.add(listener);
  return () => { control.listeners.delete(listener); };
}

/** Resolves only after the current texture is decoded and assigned; real load errors reject. */
export function whenTerraceReady(root: THREE.Object3D): Promise<void> {
  const control = controls.get(root);
  return control && !control.disposed ? control.ready : Promise.reject(new Error("Terrace disposed"));
}

export function setTerraceSkin(root: THREE.Object3D, skin: string | null): void {
  const control = controls.get(root);
  if (control && !control.disposed && control.state.skin !== skin) requestSkin(root, skin);
}

export function retryTerrace(root: THREE.Object3D): void {
  const control = controls.get(root);
  if (control && !control.disposed && control.state.status === "error") requestSkin(root, control.state.skin);
}

/** Mark dead before releasing GPU objects: a late download can never revive the terrace. */
export function disposeEnclaveTerrace(root: THREE.Object3D): void {
  const control = controls.get(root);
  if (!control || control.disposed) return;
  control.disposed = true;
  control.generation++;
  control.cancel?.();
  control.cancel = null;
  publish(root, control, "disposed");
  control.listeners.clear();
  const materials = new Set<THREE.Material>(), geometries = new Set<THREE.BufferGeometry>(), textures = new Set<THREE.Texture>();
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    geometries.add(object.geometry);
    for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
      materials.add(material);
      if (material instanceof THREE.MeshStandardMaterial && material.map) textures.add(material.map);
    }
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
  for (const texture of textures) texture.dispose();
  control.paving.map = null;
  root.clear();
  root.removeFromParent();
}
